import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { API_URL } from '../config/api';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/cart`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCart(data);
      }
    } catch (err) {
      console.error('Failed to fetch cart:', err);
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(async (productId, quantity = 1, color = 'black', services = []) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/cart/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ product_id: productId, quantity, color, services })
      });

      if (response.status === 401) {
        setLoading(false);
        return { requiresAuth: true };
      }
      
      if (!response.ok) {
        setLoading(false);
        return { error: true };
      }

      await fetchCart();
      setCartOpen(true);
      setLoading(false);
      return { success: true };
    } catch (err) {
      console.error('Failed to add to cart:', err);
      setLoading(false);
      return { error: true };
    }
  }, [fetchCart]);

  const updateQuantity = useCallback(async (productId, quantity, color = 'black') => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/cart/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ quantity, color })
      });

      if (response.ok) {
        await fetchCart();
      }
    } catch (err) {
      console.error('Failed to update cart:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  const removeFromCart = useCallback(async (productId, color = 'black') => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/cart/${productId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ color })
      });

      if (response.ok) {
        await fetchCart();
      }
    } catch (err) {
      console.error('Failed to remove from cart:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  const clearCart = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/cart/clear`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setCart({ items: [], total: 0 });
      }
    } catch (err) {
      console.error('Failed to clear cart:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyPromoCode = useCallback(async (code) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/cart/apply-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code })
      });

      const data = await response.json();
      
      if (response.ok) {
        await fetchCart();
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.detail || 'Code invalide' };
      }
    } catch (err) {
      console.error('Failed to apply promo code:', err);
      return { success: false, message: 'Erreur de connexion' };
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  const removePromoCode = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/cart/remove-promo`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchCart();
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error('Failed to remove promo code:', err);
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  const cartItemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const value = {
    cart: {
      ...cart,
      items: cart.items || []
    },
    loading,
    cartOpen,
    setCartOpen,
    fetchCart,
    addToCart,
    updateQuantity,
    updateCartItem: updateQuantity,
    removeFromCart,
    clearCart,
    applyPromoCode,
    removePromoCode,
    cartItemCount,
    itemCount: cartItemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
